'use client';

import { useTransition } from 'react';
import { deleteArticleAction } from '../../../../../actions/overview/deleteArticleAction';
import { ACTION_BUTTON_ICON_POSITION, ACTION_BUTTON_STYLE, ActionButton } from '@pixum/action-button';
import { Icon } from '@pixum/icon';
import { useCartStore, useCartErrorStore } from 'stores/cart';
import { useImageStateStore } from 'stores/express-designer';
import { CART_ARTICLE_ACTION_MODE } from '../../../../../constants/article';
import type { CartArticleActionMode } from '../../../../../types/article';
import styles from './ArticleActions.module.css';

export interface ArticleEditorActionsProps {
  articleId: number;
  articleTitle: string;
  isInactive: boolean;
  editUrl: string | null;
  copyUrl: string | null;
  mode: CartArticleActionMode;
  constants: Record<string, string>;
}

export function ArticleActions({
  isInactive,
  articleId,
  articleTitle,
  editUrl,
  copyUrl,
  mode,
  constants
}: ArticleEditorActionsProps) {
  const [isPending, startTransition] = useTransition();
  const setCartLiveValues = useCartStore(s => s.setCartLiveValues);
  const addDeletedArticleId = useCartStore(s => s.addDeletedArticleId);
  const setCartError = useCartErrorStore(s => s.setError);
  const removeProductByArticleId = useImageStateStore(s => s.removeProductByArticleId);

  if (mode === CART_ARTICLE_ACTION_MODE.READ_ONLY) return null;
  if (editUrl === null && copyUrl === null) return null;

  const editLabel = constants['i18n_cart_edit_product'] ?? 'Bearbeiten';
  const copyLabel = constants['i18n_cart_copy_product'] ?? 'Kopieren';
  const removeLabel = constants['i18n_cart_remove_product'] ?? 'Entfernen';
  const removeAriaLabel =
    constants['i18n_a11y_cart_delete_article']?.replace('{title}', articleTitle) ?? `${removeLabel} ${articleTitle}`;

  const handleRemove = () => {
    startTransition(async () => {
      const result = await deleteArticleAction(articleId);

      if (!result.success) {
        setCartError(result.error.message);

        return;
      }

      if (result.data) {
        setCartLiveValues(result.data);
      }
      addDeletedArticleId(articleId);
      removeProductByArticleId(articleId);
    });
  };

  return (
    <div className={styles['actions']}>
      {editUrl !== null && !isInactive && (
        <ActionButton
          label={editLabel}
          icon={<Icon name="edit" />}
          variant={ACTION_BUTTON_STYLE.FILLED}
          iconPosition={ACTION_BUTTON_ICON_POSITION.LEFT}
          onClick={() => {
            window.location.assign(editUrl);
          }}
        />
      )}
      {copyUrl !== null && !isInactive && (
        <ActionButton
          label={copyLabel}
          icon={<Icon name="copy" />}
          variant={ACTION_BUTTON_STYLE.FILLED}
          iconPosition={ACTION_BUTTON_ICON_POSITION.LEFT}
          onClick={() => {
            window.location.assign(copyUrl);
          }}
        />
      )}
      {isInactive && (
        <ActionButton
          label={removeLabel}
          icon={<Icon name="delete" />}
          variant={ACTION_BUTTON_STYLE.FILLED}
          iconPosition={ACTION_BUTTON_ICON_POSITION.LEFT}
          onClick={handleRemove}
          disabled={isPending}
          aria-label={removeAriaLabel}
        />
      )}
    </div>
  );
}
